import {WAMP_URL} from "../env.json";
import autobahn from "autobahn-browser/autobahn.min";

const WAMP_PREFIX_PREFIX = "@@prefix:";
const WAMP_WILDCARD_PREFIX = "@@wildcard:";

export class Wamp {
    static WAMP_URL = `${WAMP_URL}`;

    static CLOSE = Symbol('close');
    static OPEN = Symbol('open');
    static INSTANCE = null;

    _connection = null;
    _session = null;
    _sessionSubscriptions = new Map();
    _listeners = new Map();
    _accessToken = null;

    constructor() {}

    /**
     * @returns {Wamp}
     */
    static instance() {
        if (!Wamp.INSTANCE) {
            Wamp.INSTANCE = new Wamp();
            window.wamp = Wamp.INSTANCE;
        }
        return Wamp.INSTANCE;
    }

    setAccessToken(accessToken) {
        if (this._accessToken === accessToken) {
            return;
        }

        this._accessToken = accessToken;
        if (this._connection) {
            console.log("reconnecting after setting access token...");
            this._connect();
        }
    }

    /**
     * 
     * @param {string|Date|null|undefined} lastActive 
     */
    updatePresence(lastActive) {
        lastActive = lastActive || new Date();
        lastActive = lastActive instanceof Date ? lastActive : new Date(lastActive);

        return this._session?.call('com.handandfootfamilyedition.update_presence', [lastActive.toISOString()]).then((result) => {
            return result;
        }, this._session?.log);
    }

    getGameActivityCounts() {
        return this._session?.call('com.handandfootfamilyedition.get_game_activity_counts', []).then((result) => {
            return result;
        }, this._session?.log);
    }

    getPresence(username) {
        return this._session?.call('com.handandfootfamilyedition.get_presence', [username]);
    }

    _connect() {
        if (this._connection) {
            console.log("disconnecting before connecting...");
            this._disconnect();
        }

        this._connection = new autobahn.Connection({
            url: Wamp.WAMP_URL,
            realm: 'hffe',
            onchallenge: this._onChallenge.bind(this),
            authmethods: ["hffeauth"],
        });

        this._connection.onopen = this._onOpen.bind(this);
        this._connection.onclose = this._onClose.bind(this);
        this._session = null;
        this._connection.open();
    }

    _disconnect() {
        console.log("disconnecting...");
        if (this._connection) {
            console.log("closing connection...");
            this._connection.close();
        }
        this._session = null;
        this._sessionSubscriptions.clear();
        this._connection = null;
    }

    subscribe(name, listener) {
        if (!this._connection) {
            this._connect();
        }

        this._addListener(name, listener);

        if (name == Wamp.OPEN && this._session) {
            listener(this._session, false);
        }

        return () => {
            setTimeout(() => {
                this._removeListener(name, listener);
            });
        };
    }

    _onChallenge(session, method, extra) {
        console.debug("hffe auth challenge", { session, method, extra });
        if (method === "hffeauth") {
            return this._accessToken || "";
        }
        console.error("unknown challenge method", method);
    }

    _onOpen(session) {
        this._session = session;
        this._updateSessionSubscriptions();
        this._emit(Wamp.OPEN, true);
    }

    _onClose() {
        this._session = null;
        this._sessionSubscriptions.clear();
        this._emit(Wamp.CLOSE);
    }

    _emit(name, ...args) {
        console.debug("emit", { name, args });
        const listeners = this._listeners.get(name);
        if (listeners) {
            for (const listener of listeners) {
                if (listener) {
                    listener(...args);
                }
            }
        }
    }

    _addListener(name, listener) {
        console.debug("adding listener", { name, listener });
        if (!this._listeners.has(name)) {
            this._listeners.set(name, []);
        }
        this._listeners.get(name).push(listener);
        this._updateSessionSubscriptions();
    }

    _removeListener(name, listener) {
        console.debug("removing listener", { name, listener });
        if (!this._listeners.has(name)) {
            return;
        }
        const listeners = this._listeners.get(name);
        const index = listeners.indexOf(listener);
        if (index >= 0) {
            listeners.splice(index, 1);
        }
        this._updateSessionSubscriptions();
    }

    _updateSessionSubscriptions() {
        if (!this._session) {
            return;
        }

        for (const name of this._listeners.keys()) {
            if (name === Wamp.OPEN || name === Wamp.CLOSE) {
                continue;
            }

            const subscriberCount = this._listeners.get(name).length;
            if (subscriberCount === 0 && this._sessionSubscriptions.has(name)) {
                const unsubscribe = this._sessionSubscriptions.get(name);
                this._sessionSubscriptions.delete(name);
                unsubscribe();
                continue;
            }

            if (this._sessionSubscriptions.has(name) || subscriberCount <= 0) {
                continue;
            }

            let subname = name;
            let options = undefined;
            if (name.indexOf(WAMP_PREFIX_PREFIX) === 0) {
                subname = name.substring(WAMP_PREFIX_PREFIX.length);
                options = { match: "prefix" };
            } else if (name.indexOf(WAMP_WILDCARD_PREFIX) === 0) {
                subname = name.substring(WAMP_WILDCARD_PREFIX.length);
                options = { match: "wildcard" };
            }

            console.debug("adding subscription", {subname, options, name});
            this._sessionSubscriptions.set(name, () => false);
            this._session.subscribe(subname, (...args) => {
                this._emit(name, ...args);
            }, options).then((subscription) => {
                this._sessionSubscriptions.set(name, () => {
                    if (!this._session) {
                        return false;
                    }
                    this._session.unsubscribe(subscription).then(() => {
                        console.debug("unsubscribed", { name, subscription });
                    }).catch((e) => {
                        console.error("unsubscribe error", { name, subscription, e });
                    });
                });
            });
        }
    }

    static prefix(name) {
        return WAMP_PREFIX_PREFIX + name;
    }

    static wildcard(name) {
        return WAMP_WILDCARD_PREFIX + name;
    }
}

export function subscribeToNotifications(onNotification) {
    const wamp = Wamp.instance();
    const prefix = Wamp.prefix(`com.handandfootfamilyedition.notifications`);
    return wamp.subscribe(prefix, (args) => { onNotification(args); });
}

export function subscribeToGame(gameId, teamPlayerId, onGameMessage = () => {}, onPlayerMessage = () => {}, onOpen = () => {}, onClose = () => {}) {
    const wamp = Wamp.instance();

    const unsubFromGame = wamp.subscribe(`com.handandfootfamilyedition.game.${gameId}`, onGameMessage);
    const unsubFromPlayer =  wamp.subscribe(`com.handandfootfamilyedition.player.messages.${teamPlayerId}`, onPlayerMessage);
    const unsubFromOpen = wamp.subscribe(Wamp.OPEN, onOpen);
    const unsubFromClose = wamp.subscribe(Wamp.CLOSE, onClose);

    return () => {
        unsubFromGame();
        unsubFromPlayer();
        unsubFromOpen();
        unsubFromClose();
    };
}

export function subscribeToChatUpdates(onChat = () => {}) {
    const wamp = Wamp.instance();
    const prefix = Wamp.prefix(`com.handandfootfamilyedition.notifications.chat`);
    return wamp.subscribe(prefix, (args) => { onChat(args); });
}

export function subscribeToPresenceUpdates(userId, onPresence = () => {}) {
    const wamp = Wamp.instance();
    const name = `com.handandfootfamilyedition.presence.${userId}`;
    return wamp.subscribe(name, (args) => { onPresence(args); });
}

export function continuouslyPublishPresence() {
    const wamp = Wamp.instance();

    let lastActive = new Date();

    wamp.updatePresence(lastActive);
    const updatePresenceInterval = setInterval(() => {
        wamp.updatePresence(lastActive);
    }, 10000);

    const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'touchend'];
    const updateLastActive = () => {
        lastActive = new Date();
    };
    EVENTS.forEach(event => window.addEventListener(event, updateLastActive));


    return () => {
        clearInterval(updatePresenceInterval);
        EVENTS.forEach(event => window.removeEventListener(event, updateLastActive));
    };
}

export function subscribeToOpenGameEvents(onOpenGameEvent = () => {}) {
    const wamp = Wamp.instance();
    const prefix = Wamp.prefix(`com.handandfootfamilyedition.opengame`);
    return wamp.subscribe(prefix, (args) => { onOpenGameEvent(args); });
}